// ============================================================
// SIDEBAR — グループ化されたナビ（業務 / 情報 / 役員 / 管理）。
// 役員グループは isExec、管理グループは isAdmin でゲートする
// （権限のない閲覧ユーザーには項目自体を出さない＝UI可視性レイヤー）。
// <768px は off-canvas ドロワー（.sidebar-drawer + .open、index.css 制御）。
// 暗色サーフェス（.dark-surface）+ NavLink の aria-current でアクティブ表示。
// ============================================================
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
// apple-design 第3段: ドロワーは Motion が transform を所有（CSS transition は index.css から撤去済み）。
// モバイルでは drag="x" で「掴んで閉じられる」＋離した速度で閉じ判定（velocity handoff）。
import { m, useAnimationControls } from 'motion/react';
import type { PanInfo } from 'motion/react';
import { useViewer } from '../auth/AuthContext';
import { isExec, isAdmin } from '../lib/permissions';
import { auth } from '../lib/firebase';

/** ドロワー幅(px) = w-64(256px) + 影の余裕。閉位置 x = -DRAWER_W。 */
const DRAWER_W = 264;
/** 臨界減衰スプリング（apple-design: 既定はovershootなし）。 */
const DRAWER_SPRING = { type: 'spring', bounce: 0, duration: 0.35 } as const;

interface NavItem {
  to: string;
  label: string;
  /** index ルート（exact 一致が必要）。 */
  end?: boolean;
}
interface NavGroup {
  heading: string;
  items: NavItem[];
  /** 表示ゲート（無ければ常時表示）。 */
  gate?: 'exec' | 'admin';
}

// ホームは単独でナビ最上に出す（どのグループにも属さない入口）。
const HOME_ITEM: NavItem = { to: '/', label: 'ホーム', end: true };

const NAV_GROUPS: NavGroup[] = [
  {
    // 会議→記録→自分の作業→予定、という日々の作業の流れ。
    heading: '議事録・タスク',
    items: [
      { to: '/notes', label: '議事録' },
      { to: '/upload', label: 'アップロード' },
      { to: '/tasks', label: 'マイタスク' },
      { to: '/calendar', label: 'カレンダー' },
    ],
  },
  {
    // 横断的に「見る・聞く」ツール。
    heading: 'プロジェクト・AI',
    items: [
      { to: '/projects', label: 'プロジェクト' },
      { to: '/assistant', label: 'AIに聞く' },
      { to: '/ax-report', label: 'AX入力' },
    ],
  },
  {
    // 参照する社内資産。旧5画面（お知らせ/ナレッジ・手順/プロンプト集/作品ギャラリー/AX成功事例）は
    // /knowledge の3タブに統合（2026-07-02 IA再編。お知らせは Slack に一本化して廃止）。
    heading: '社内情報',
    items: [{ to: '/knowledge', label: 'ナレッジ' }],
  },
  {
    heading: '組織',
    gate: 'exec',
    items: [
      { to: '/oneonone', label: '1on1メンタリング' },
      { to: '/ax', label: 'AX推進' },
      { to: '/drive-activity', label: '作業ログ' },
    ],
  },
  {
    heading: '管理',
    gate: 'admin',
    items: [
      { to: '/members', label: 'メンバー比較' },
      { to: '/settings', label: '権限・設定' },
    ],
  },
];

// Creative Studio（社内制作アプリ）のURL。ビルド時に VITE_STUDIO_URL が注入されたときだけ
// サイドバーに入口を出す（未設定＝未公開の環境ではリンク自体を出さない）。
const STUDIO_URL = import.meta.env.VITE_STUDIO_URL;

/** ログイン中ならFirebase IDトークンを付けてスタジオを新規タブで開く（SSOハンドオフ）。
 *  トークン取得に失敗・DEVモード時は素のURLで開く（スタジオ側が未認証案内を出す）。 */
async function openStudio(): Promise<void> {
  if (!STUDIO_URL) return;
  let target = STUDIO_URL;
  try {
    const idToken = auth?.currentUser ? await auth.currentUser.getIdToken() : null;
    if (idToken) {
      const url = new URL(STUDIO_URL);
      url.searchParams.set('fb_token', idToken);
      target = url.toString();
    }
  } catch {
    // トークンが取れなくても入口は開く（スタジオ側の /unauthorized が案内する）。
  }
  window.open(target, '_blank', 'noopener');
}

function navLinkClass({ isActive }: { isActive: boolean }): string {
  const base =
    'nav-item dark-surface block rounded-soft px-3 py-2 text-sm font-medium outline-none';
  return isActive
    ? `${base} bg-white/15 text-white`
    : `${base} text-white/70 hover:bg-white/10 hover:text-white`;
}

export default function Sidebar({
  drawerOpen,
  onClose,
}: {
  drawerOpen: boolean;
  onClose: () => void;
}) {
  const viewer = useViewer();

  // <768px 判定（ドロワー挙動はモバイルのみ。デスクトップは常に x=0 の静的サイドバー）。
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // open/close は controls で駆動（drag 中断からでも現在位置＋速度から再ターゲット＝中断可能）。
  const controls = useAnimationControls();
  useEffect(() => {
    void controls.start({ x: isMobile && !drawerOpen ? -DRAWER_W : 0 }, DRAWER_SPRING);
  }, [controls, isMobile, drawerOpen]);

  // 離した瞬間の速度 or 位置で閉じ判定。閉じないなら開位置へスプリングで復帰。
  const onDragEnd = (_e: unknown, info: PanInfo) => {
    if (info.velocity.x < -250 || info.offset.x < -DRAWER_W / 2) onClose();
    else void controls.start({ x: 0 }, DRAWER_SPRING);
  };

  const groups = NAV_GROUPS.filter((g) => {
    if (g.gate === 'exec') return isExec(viewer);
    if (g.gate === 'admin') return isAdmin(viewer);
    return true;
  });

  return (
    <>
      {/* mobile overlay — タップで閉じる */}
      <div
        className={`sidebar-overlay${drawerOpen ? ' open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <m.aside
        initial={{ x: isMobile && !drawerOpen ? -DRAWER_W : 0 }}
        animate={controls}
        drag={isMobile ? 'x' : false}
        dragConstraints={{ left: -DRAWER_W, right: 0 }}
        dragElastic={{ left: 0, right: 0.06 }}
        dragMomentum={false}
        onDragEnd={onDragEnd}
        className={`dark-surface material-dark sidebar-drawer${drawerOpen ? ' open' : ''} w-64 shrink-0 bg-charcoal text-white flex flex-col md:static md:translate-x-0`}
        aria-label="サイドナビゲーション"
      >
        {/* brand + mobile close */}
        <div className="flex items-center justify-between px-4 h-16 border-b border-white/10">
          <div className="leading-tight">
            <div className="text-sm font-bold tracking-wide">TEMP 社内ポータル</div>
            <div className="text-2xs text-white/55">Team Portal</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="メニューを閉じる"
            className="dark-surface md:hidden rounded-soft p-1.5 text-white/70 hover:bg-white/10 hover:text-white"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* groups */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {/* ホーム（単独・最上・見出しなし） */}
          <ul className="space-y-0.5">
            <li>
              <NavLink to={HOME_ITEM.to} end={HOME_ITEM.end} className={navLinkClass} onClick={onClose}>
                {HOME_ITEM.label}
              </NavLink>
            </li>
          </ul>
          {groups.map((g) => (
            // 各グループは上に区切り線＋余白でセクション化（羅列感の解消）。
            <div key={g.heading} className="pt-4 mt-1 border-t border-white/[0.08]">
              {/* 見出し：アンバーのアクセントバー＋太字・明るめで強調。権限ゲート群は鍵🔒 */}
              <div className="px-3 mb-1.5 flex items-center gap-1.5">
                <span className="w-1 h-3.5 rounded-full bg-amber-brand" aria-hidden="true" />
                <span className="text-2xs font-bold tracking-wide text-white/75">{g.heading}</span>
                {g.gate && (
                  <svg
                    width="11" height="11" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                    className="text-white/45" aria-label="権限制限あり"
                  >
                    <rect x="3" y="11" width="18" height="10" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                )}
              </div>
              <ul className="space-y-0.5">
                {g.items.map((item) => (
                  <li key={item.to}>
                    <NavLink to={item.to} end={item.end} className={navLinkClass} onClick={onClose}>
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* 制作ツール（外部アプリ）— VITE_STUDIO_URL 設定時のみ表示。
              ログイン中のIDトークンを付けて新規タブで開く（SSOハンドオフ）。 */}
          {STUDIO_URL && (
            <div className="pt-4 mt-1 border-t border-white/[0.08]">
              <div className="px-3 mb-1.5 flex items-center gap-1.5">
                <span className="w-1 h-3.5 rounded-full bg-amber-brand" aria-hidden="true" />
                <span className="text-2xs font-bold tracking-wide text-white/75">制作ツール</span>
              </div>
              <ul className="space-y-0.5">
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      void openStudio();
                    }}
                    className="nav-item dark-surface block w-full text-left rounded-soft px-3 py-2 text-sm font-medium outline-none text-white/70 hover:bg-white/10 hover:text-white"
                  >
                    制作スタジオ
                    <span className="ml-1.5 text-white/45" aria-hidden="true">↗</span>
                  </button>
                </li>
              </ul>
            </div>
          )}
        </nav>

        {/* viewer footer */}
        <div className="px-4 py-3 border-t border-white/10">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center w-8 h-8 rounded-full bg-white/15 text-sm font-bold">
              {viewer.initials}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{viewer.name}</div>
              <div className="text-2xs text-white/50">
                {viewer.role === 'admin' ? '管理者' : 'メンバー'}
                {viewer.exec ? '・役員' : ''}
              </div>
            </div>
          </div>
        </div>
      </m.aside>
    </>
  );
}
